import { buildCampaignBuilderContextProps } from '../builders/buildCampaignBuilderContextProps';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTY_KEYS, COMPONENT_OUTCOMES, DCM_PAGES, UPLOAD_SOURCES } from '../constants';
import { trackAnalyticsEvent } from '../core/analyticsClient';
import type { AnalyticsEventProperties } from '../types';
import type { TargetingCategory } from '../utils/getAppliedTargetingCategories';

export type PostEditEventContext = {
  campaignId?: string;
  lineItemId?: string;
  adAccountId?: string;
  revisionNumber?: string;
  isValidatedEdit?: boolean;
  editType: string;
  politicalClassification?: string;
  industryVertical?: string;
  userRole?: string;
};

const buildPostEditProps = ({
  campaignId,
  lineItemId,
  adAccountId,
  revisionNumber,
  isValidatedEdit,
  editType,
  politicalClassification,
  industryVertical,
  userRole,
}: PostEditEventContext): AnalyticsEventProperties => ({
  [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
  [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
  [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
  [ANALYTICS_PROPERTY_KEYS.REVISION_NUMBER]: revisionNumber,
  [ANALYTICS_PROPERTY_KEYS.IS_VALIDATED_EDIT]: isValidatedEdit,
  [ANALYTICS_PROPERTY_KEYS.EDIT_TYPE]: editType,
  [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_BUILDER,
  [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
  ...buildCampaignBuilderContextProps({
    politicalClassification,
    industryVertical,
    userRole,
  }),
});

export const trackPostSubmitEdit = (params: PostEditEventContext): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.POST_SUBMIT_EDIT, buildPostEditProps(params));
};

export type TrackPostLaunchBudgetEditParams = PostEditEventContext & {
  fromBudget?: number | null;
  toBudget?: number | null;
};

export const trackPostLaunchBudgetEdit = ({
  fromBudget,
  toBudget,
  ...params
}: TrackPostLaunchBudgetEditParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.POST_LAUNCH_BUDGET_EDIT, {
    ...buildPostEditProps(params),
    [ANALYTICS_PROPERTY_KEYS.FROM_BUDGET]: fromBudget ?? undefined,
    [ANALYTICS_PROPERTY_KEYS.TO_BUDGET]: toBudget ?? undefined,
    [ANALYTICS_PROPERTY_KEYS.BUDGET]: toBudget ?? undefined,
    [ANALYTICS_PROPERTY_KEYS.BUDGET_AMOUNT]: toBudget ?? undefined,
  });
};

export type TrackPostLaunchDateEditParams = PostEditEventContext & {
  fromStartDate?: string;
  toStartDate?: string;
  fromEndDate?: string;
  toEndDate?: string;
};

export const trackPostLaunchDateEdit = ({
  fromStartDate,
  toStartDate,
  fromEndDate,
  toEndDate,
  ...params
}: TrackPostLaunchDateEditParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.POST_LAUNCH_DATE_EDIT, {
    ...buildPostEditProps(params),
    [ANALYTICS_PROPERTY_KEYS.FROM_START_DATE]: fromStartDate,
    [ANALYTICS_PROPERTY_KEYS.TO_START_DATE]: toStartDate,
    [ANALYTICS_PROPERTY_KEYS.FROM_END_DATE]: fromEndDate,
    [ANALYTICS_PROPERTY_KEYS.TO_END_DATE]: toEndDate,
    [ANALYTICS_PROPERTY_KEYS.START_DATE]: toStartDate,
    [ANALYTICS_PROPERTY_KEYS.END_DATE]: toEndDate,
  });
};

export type TrackPostLaunchTargetingEditParams = PostEditEventContext & {
  fromTargetingTypes: TargetingCategory[];
  toTargetingTypes: TargetingCategory[];
};

export const trackPostLaunchTargetingEdit = ({
  fromTargetingTypes,
  toTargetingTypes,
  ...params
}: TrackPostLaunchTargetingEditParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.POST_LAUNCH_TARGETING_EDIT, {
    ...buildPostEditProps(params),
    [ANALYTICS_PROPERTY_KEYS.FROM_TARGETING_TYPES]: fromTargetingTypes,
    [ANALYTICS_PROPERTY_KEYS.TO_TARGETING_TYPES]: toTargetingTypes,
    [ANALYTICS_PROPERTY_KEYS.TARGETING_TYPES]: toTargetingTypes,
  });
};

export type TrackPostLaunchCreativeSwapParams = PostEditEventContext & {
  creativeId: string;
  uploadSource?: (typeof UPLOAD_SOURCES)[keyof typeof UPLOAD_SOURCES];
};

export const trackPostLaunchCreativeSwap = ({
  creativeId,
  uploadSource,
  ...params
}: TrackPostLaunchCreativeSwapParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.POST_LAUNCH_CREATIVE_SWAP, {
    ...buildPostEditProps(params),
    [ANALYTICS_PROPERTY_KEYS.CREATIVE_ID]: creativeId,
    [ANALYTICS_PROPERTY_KEYS.UPLOAD_SOURCE]: uploadSource,
  });
};
